import clsx from 'clsx'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Inbox } from 'lucide-react'
import Button from './Button'
import { Skeleton } from './Skeleton'

/**
 * Data table.
 *
 * A real <table> rather than a grid of divs, so screen readers announce row and
 * column relationships and sort state, and so the browser's own find-in-page
 * works on the contents.
 *
 * Below the `sm` breakpoint the same data renders as stacked cards. A table
 * squeezed onto a 375px screen either scrolls horizontally — which the layout
 * rules forbid — or truncates every column into uselessness, and most of this
 * workforce is on a phone.
 */

export function DataTable({
  columns,
  rows,
  keyField = 'id',
  loading = false,
  onRowClick,
  sort,
  onSortChange,
  empty,
  mobileCard,
  className,
  rowHeight = 45,
}) {
  if (loading) return <TableSkeleton columns={columns.length} rowHeight={rowHeight} />

  if (!rows?.length) {
    return (
      <div className="flex flex-1 items-center justify-center">
        {empty ?? <EmptyState title="Nothing to show" />}
      </div>
    )
  }

  const clickable = typeof onRowClick === 'function'

  return (
    <div className={clsx('flex min-h-0 flex-1 flex-col', className)}>
      {/*
       * Desktop scroll container.
       *
       * flex-1 min-h-0 rather than a fixed max-height: the table fills
       * whatever space its flex ancestors give it (a page opting in with
       * h-full flex flex-col, a card with flex-1 min-h-0 of its own) and
       * scrolls internally once content exceeds that, with the header
       * staying pinned via `sticky`. A page that is not part of such a
       * column — most forms and detail views — never sets that up, so this
       * div simply sizes to its content and the page scrolls as it always
       * did; nothing here forces every table onto a fixed-height layout.
       */}
      <div className="hidden min-h-0 flex-1 overflow-y-auto overflow-x-auto scroll-slim sm:block">
        <table className="w-full border-collapse text-sm">
          {/* Sticky so the header stays visible while a long result set
              scrolls beneath it, rather than scrolling off with row one. */}
          <thead className="sticky top-0 z-10 bg-[var(--color-surface)]">
            <tr className="border-b border-[var(--color-line)]">
              {columns.map((column) => {
                const sortable = column.sortable && onSortChange
                const active = sort?.field === column.key

                return (
                  <th
                    key={column.key}
                    scope="col"
                    // aria-sort is what tells a screen-reader user how the
                    // table is currently ordered.
                    aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                    style={{ width: column.width }}
                    className={clsx(
                      'px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide',
                      'text-[var(--color-ink-500)] whitespace-nowrap',
                      column.align === 'right' && 'text-right',
                      column.align === 'center' && 'text-center',
                    )}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() =>
                          onSortChange({
                            field: column.key,
                            direction: active && sort.direction === 'desc' ? 'asc' : 'desc',
                          })
                        }
                        className="inline-flex cursor-pointer items-center gap-1 hover:text-[var(--color-ink-900)]"
                      >
                        {column.header}
                        {active ? (
                          sort.direction === 'asc'
                            ? <ArrowUp size={12} aria-hidden="true" />
                            : <ArrowDown size={12} aria-hidden="true" />
                        ) : (
                          <ArrowDown size={12} className="opacity-25" aria-hidden="true" />
                        )}
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr
                key={row[keyField]}
                onClick={clickable ? () => onRowClick(row) : undefined}
                // Keyboard parity: a row that responds to a click must respond
                // to Enter, and must be reachable by Tab.
                tabIndex={clickable ? 0 : undefined}
                role={clickable ? 'button' : undefined}
                onKeyDown={
                  clickable
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          onRowClick(row)
                        }
                      }
                    : undefined
                }
                className={clsx(
                  // Every row keeps its bottom border, including the last —
                  // this table can now sit as a short block above a visibly
                  // separate Pagination footer rather than always meeting a
                  // card's own bottom edge, and the last row's line is what
                  // closes off the table cleanly in that case instead of the
                  // row just trailing into empty space.
                  'border-b border-[var(--color-line)]',
                  clickable &&
                    'cursor-pointer transition-colors duration-150 hover:bg-[var(--color-surface-sunken)]',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={clsx(
                      'px-3 py-3 text-[var(--color-ink-900)] align-middle',
                      column.align === 'right' && 'text-right',
                      column.align === 'center' && 'text-center',
                      column.numeric && 'tnum',
                      column.nowrap && 'whitespace-nowrap',
                    )}
                  >
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Mobile ────────────────────────────────────────────────────────── */}
      <div className="divide-y divide-[var(--color-line)] sm:hidden">
        {rows.map((row) => (
          <div
            key={row[keyField]}
            onClick={clickable ? () => onRowClick(row) : undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={
              clickable
                ? (event) => {
                    if (event.key === 'Enter') onRowClick(row)
                  }
                : undefined
            }
            className={clsx('px-4 py-3.5', clickable && 'cursor-pointer active:bg-[var(--color-surface-sunken)]')}
          >
            {mobileCard ? mobileCard(row) : <DefaultMobileCard row={row} columns={columns} />}
          </div>
        ))}
      </div>
    </div>
  )
}

function DefaultMobileCard({ row, columns }) {
  const [primary, ...rest] = columns

  return (
    <div className="space-y-1.5">
      <div className="font-medium text-[var(--color-ink-900)]">
        {primary.render ? primary.render(row) : row[primary.key]}
      </div>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        {rest.slice(0, 4).map((column) => (
          <div key={column.key} className="min-w-0">
            <dt className="text-[var(--color-ink-500)]">{column.header}</dt>
            <dd className="truncate text-[var(--color-ink-900)]">
              {column.render ? column.render(row) : row[column.key]}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

/**
 * Pagination.
 *
 * Shows the range and the total, not just page numbers. "Showing 1–25 of 312"
 * answers the question people actually have, which is how much there is.
 *
 * Always rendered once a result set has loaded, even when everything fits on
 * one page — it is the table's fixed bottom frame now that the table itself
 * holds a constant height, and a frame that only sometimes has a bottom edge
 * reads as inconsistent. The row-count text stays; only the page-navigation
 * controls (prev/next/numbers) disappear when there is nothing to page to.
 *
 * Sits on a faint sunken tint rather than the card's own surface colour —
 * the same token filter bars already use — so this footer reads as a fixed
 * part of the table's frame rather than another row of content. `rounded-b-lg`
 * matches the card radius so the tint does not visibly square off the card's
 * corners.
 */
export function Pagination({
  meta,
  onPageChange,
  perPage,
  onPerPageChange,
  perPageOptions = [10, 25, 50, 100],
  className,
}) {
  if (!meta) return null

  const { current_page: page, last_page: lastPage, from, to, total } = meta

  // A compact page strip rather than every page number: for the case counts
  // this portal deals with, ellipsis logic for forty page buttons is
  // machinery nobody asked for. Current, one either side, first and last —
  // enough to jump nearby or to either end without hunting for a text field.
  const nearby = [page - 1, page, page + 1].filter((p) => p >= 1 && p <= lastPage)
  const pages = [...new Set([1, ...nearby, lastPage])].sort((a, b) => a - b)

  return (
    <div
      className={clsx(
        'flex shrink-0 flex-col gap-3 rounded-b-lg border-t border-[var(--color-line)]',
        'bg-[var(--color-surface-sunken)] px-4 py-2.5',
        'sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        {/*
         * The page-size control, offered only when the caller actually wires
         * one up (onPerPageChange) — most pages that use Pagination never had
         * a way to change per_page at all, and showing a selector that does
         * nothing would be worse than not having it.
         *
         * 100 is offered as "100" rather than an unbounded "All": the backend
         * caps per_page at 100 regardless (min(100, ...) on every paginated
         * endpoint), and a page-size option that silently clamps to a much
         * smaller number than it promises is its own kind of bug.
         */}
        {onPerPageChange && (
          <label className="flex items-center gap-1.5 text-xs text-[var(--color-ink-500)]">
            Show
            <select
              value={perPage}
              onChange={(event) => onPerPageChange(Number(event.target.value))}
              className="h-7 cursor-pointer rounded-md border border-[var(--color-line-strong)] bg-[var(--color-surface)] px-1.5 text-xs text-[var(--color-ink-900)]"
            >
              {perPageOptions.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </label>
        )}

        <p className="text-xs text-[var(--color-ink-500)] tnum">
          Showing <span className="font-medium text-[var(--color-ink-700)]">{from ?? 0}–{to ?? 0}</span> of{' '}
          <span className="font-medium text-[var(--color-ink-700)]">{total}</span>
        </p>
      </div>

      {/*
       * Always rendered, even on a single page — a control that only
       * sometimes appears reads as broken rather than as absent, and prev,
       * next and the lone "1" step are simply disabled/inert when there is
       * nowhere else to go. This is the same reasoning as the row-count text
       * beside it: a consistent footer frame regardless of result size.
       */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          icon={ChevronLeft}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
          className="h-7 w-7 p-0"
        />

        <div className="mx-1 flex items-center gap-0.5">
          {pages.map((p, index) => (
            <PageStep
              key={p}
              page={p}
              active={p === page}
              // A gap in the sequence (e.g. 1 … 6 7 8 … 24) gets an
              // ellipsis rather than every page in between, which is the
              // whole point of not rendering all forty buttons.
              gapBefore={index > 0 && p - pages[index - 1] > 1}
              onClick={() => onPageChange(p)}
            />
          ))}
        </div>

        <Button
          variant="ghost"
          icon={ChevronRight}
          disabled={page >= lastPage}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
          className="h-7 w-7 p-0"
        />
      </div>
    </div>
  )
}

function PageStep({ page, active, gapBefore, onClick }) {
  return (
    <>
      {gapBefore && (
        <span className="px-1 text-xs text-[var(--color-ink-400)]" aria-hidden="true">…</span>
      )}
      <button
        type="button"
        onClick={onClick}
        disabled={active}
        aria-current={active ? 'page' : undefined}
        className={clsx(
          'flex h-7 min-w-7 items-center justify-center rounded-md px-1.5 text-xs font-medium tnum transition-colors duration-150',
          active
            ? 'bg-[var(--color-accent)] text-white'
            : 'cursor-pointer text-[var(--color-ink-700)] hover:bg-[var(--color-surface)]',
        )}
      >
        {page}
      </button>
    </>
  )
}

/**
 * Empty state.
 *
 * Always says what to do next, never just "no data". An empty queue and a
 * filter that matched nothing are different situations and should read
 * differently — the caller passes the right message for each.
 */
export function EmptyState({ icon: Icon = Inbox, title, description, action, className }) {
  return (
    <div className={clsx('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--color-surface-sunken)]">
        <Icon size={20} className="text-[var(--color-ink-400)]" aria-hidden="true" />
      </div>

      <h3 className="text-sm font-semibold text-[var(--color-ink-900)]">{title}</h3>

      {description && (
        <p className="mt-1 max-w-sm text-sm text-[var(--color-ink-500)]">{description}</p>
      )}

      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

function TableSkeleton({ columns, rowHeight }) {
  return (
    <div className="flex-1 px-4 py-2">
      {Array.from({ length: 8 }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          style={{ minHeight: rowHeight }}
          className="flex items-center gap-4 border-b border-[var(--color-line)] py-3 last:border-0"
        >
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className="h-4 flex-1"
              // Varying the widths stops the placeholder reading as a grid of
              // identical bars, which looks like a rendering fault.
              style={{ maxWidth: columnIndex === 0 ? '20%' : `${45 + ((columnIndex * 17) % 40)}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}
